/**
 * React Query client configuration
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { QueryClientConfig } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ApiError } from '../api/user-portal-api-generated';
import { HttpStatus } from '../enums/http-status.enums';
import { handleUnauthorized } from './auth-utils';

export function getStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.status;
  }

  if (error instanceof AxiosError) {
    return error.response?.status;
  }

  return undefined;
}

// Default configuration for React Query
const queryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute
      staleTime: 1000 * 60,
      // Cache data for 5 minutes
      gcTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        const status = getStatus(error);

        // Don't retry on 4xx errors (client errors, including 401)
        if (
          status &&
          status >= HttpStatus.BAD_REQUEST &&
          status < HttpStatus.INTERNAL_SERVER_ERROR
        ) {
          return false;
        }

        return failureCount < 3;
      },
      // Exponential backoff for retries
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
  // QueryCache/MutationCache.onError fires for every query/mutation in
  // addition to any per-call onError, so 401 handling isn't lost when a
  // caller defines its own onError (e.g. toast notifications).
  queryCache: new QueryCache({
    onError: error => {
      if (getStatus(error) === HttpStatus.UNAUTHORIZED) {
        handleUnauthorized();
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: error => {
      if (getStatus(error) === HttpStatus.UNAUTHORIZED) {
        handleUnauthorized();
      }
    },
  }),
};

// Create the query client instance
export const queryClient = new QueryClient(queryConfig);

// Query key factory for consistent query keys
export const queryKeys = {
  all: ['user-portal'] as const,

  applications: {
    all: () => [...queryKeys.all, 'applications'] as const,
    list: () => [...queryKeys.applications.all(), 'list'] as const,
  },

  devices: {
    all: () => [...queryKeys.all, 'devices'] as const,
    list: () => [...queryKeys.devices.all(), 'list'] as const,
  },

  enrolledFactors: {
    all: () => [...queryKeys.all, 'enrolled-factors'] as const,
  },

  docs: {
    all: () => [...queryKeys.all, 'docs'] as const,
    content: (path: string) =>
      [...queryKeys.docs.all(), 'content', path] as const,
    navigation: () => [...queryKeys.docs.all(), 'navigation'] as const,
    adminNavigation: () =>
      [...queryKeys.docs.all(), 'admin-navigation'] as const,
  },

  devicePairing: {
    all: () => [...queryKeys.all, 'devicePairing'] as const,
    status: (pairingCode: string) =>
      [...queryKeys.devicePairing.all(), 'status', pairingCode] as const,
  },

  sessions: {
    all: () => [...queryKeys.all, 'sessions'] as const,
    active: () => [...queryKeys.sessions.all(), 'active'] as const,
  },

  connectedApps: {
    all: () => [...queryKeys.all, 'connectedApps'] as const,
    list: () => [...queryKeys.connectedApps.all(), 'list'] as const,
  },

  agents: {
    all: () => [...queryKeys.all, 'agents'] as const,
    list: () => [...queryKeys.agents.all(), 'list'] as const,
    approvals: () => [...queryKeys.agents.all(), 'approvals'] as const,
    gatewayClients: () =>
      [...queryKeys.agents.all(), 'gatewayClients'] as const,
    activity: (agentId: string, status?: string) =>
      [
        ...queryKeys.agents.all(),
        agentId,
        'activity',
        status ?? 'all',
      ] as const,
    heatmap: (agentId: string) =>
      [...queryKeys.agents.all(), agentId, 'heatmap'] as const,
    sessions: (agentId: string) =>
      [...queryKeys.agents.all(), agentId, 'sessions'] as const,
  },

  escalation: {
    all: () => [...queryKeys.all, 'escalation'] as const,
    check: (operation: string) =>
      [...queryKeys.escalation.all(), 'check', operation] as const,
  },
};
